import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["customBoxShadow", "padding", "paddingBlock", "paddingInline", "scrollSnapAlign"];
import { useTheme } from 'foundations-theming/utils';
import { PPUIGridItem } from 'pricing-pages-ui-components/grid/PPUIGridItem';
import { useMerchCardGridLayout } from './MerchCardGridLayout';

/**
 * Styles that are applied as if it's to the entire card, not just a single grid cell.
 */
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const MerchCardLayout = ({
  'data-selenium-id': dataSeleniumId,
  columnStart,
  Badge,
  Title,
  Subheading,
  Pricing,
  CTAs,
  Subtext,
  Seats,
  Term,
  Credits,
  Features,
  Footer,
  cardStyles,
  isIneligible,
  Overlay
}) => {
  var _cardStylesTokens$bor, _cardStylesTokens$bor2, _cardStylesTokens$bor3, _cardStylesTokens$bor4;
  const {
    gridAreaNames
  } = useMerchCardGridLayout();
  const theme = useTheme();
  const layoutProps = {
    gridColumn: `${columnStart} / span 1`
  };
  const _ref = cardStyles || {},
    {
      customBoxShadow,
      padding,
      paddingBlock,
      paddingInline,
      scrollSnapAlign = 'center'
    } = _ref,
    cardStylesTokens = _objectWithoutPropertiesLoose(_ref, _excluded);
  let cssBoxShadow;
  if (customBoxShadow) {
    const {
      offsetX,
      offsetY,
      blur,
      spread,
      color
    } = customBoxShadow;
    cssBoxShadow = `${offsetX} ${offsetY} ${blur} ${spread} ${theme.color[color]}`;
  }
  const nonInteractiveProps = isIneligible ? {
    pointerEvents: 'none',
    'aria-hidden': true
  } : {};
  const styleProps = Object.assign({
    paddingInline: paddingInline !== null && paddingInline !== void 0 ? paddingInline : padding
  }, nonInteractiveProps);
  const getGridItem = (name, overrideStyles) => {
    // NOTE: Avoid setting non-layout styling (padding, margin, backgroundColor, etc.) on these grid items
    // Instead, apply those styles to the components passed to the grid item
    switch (name) {
      case 'Badge':
        return /*#__PURE__*/_jsx(PPUIGridItem, Object.assign({
          gridArea: name,
          position: "relative"
        }, layoutProps, styleProps, overrideStyles, {
          children: Badge
        }), name);
      case 'Title':
        return /*#__PURE__*/_jsx(PPUIGridItem, Object.assign({
          gridArea: name,
          position: "relative",
          "data-selenium-id": dataSeleniumId
        }, layoutProps, styleProps, overrideStyles, {
          children: Title
        }), name);
      case 'Subheading':
        return /*#__PURE__*/_jsx(PPUIGridItem, Object.assign({
          gridArea: name
        }, layoutProps, styleProps, overrideStyles, {
          children: Subheading
        }), name);
      case 'Pricing':
        return /*#__PURE__*/_jsx(PPUIGridItem, Object.assign({
          gridArea: name
        }, layoutProps, styleProps, overrideStyles, {
          children: Pricing
        }), name);
      case 'Seats':
        return /*#__PURE__*/_jsx(PPUIGridItem, Object.assign({
          gridArea: name
        }, layoutProps, styleProps, overrideStyles, {
          children: Seats
        }), name);
      case 'Term':
        return /*#__PURE__*/_jsx(PPUIGridItem, Object.assign({
          gridArea: name
        }, layoutProps, styleProps, overrideStyles, {
          children: Term
        }), name);
      case 'CTAs':
        return /*#__PURE__*/_jsx(PPUIGridItem, Object.assign({
          gridArea: name
        }, layoutProps, styleProps, overrideStyles, {
          children: CTAs
        }), name);
      case 'Credits':
        return /*#__PURE__*/_jsx(PPUIGridItem, Object.assign({
          gridArea: name
        }, layoutProps, styleProps, overrideStyles, {
          children: Credits
        }), name);
      case 'Subtext':
        return /*#__PURE__*/_jsx(PPUIGridItem, Object.assign({
          gridArea: name
        }, layoutProps, styleProps, overrideStyles, {
          children: Subtext
        }), name);
      case 'Features':
        return /*#__PURE__*/_jsx(PPUIGridItem, Object.assign({
          gridArea: name
        }, layoutProps, styleProps, overrideStyles, {
          children: Features
        }), name);
      case 'Footer':
        return /*#__PURE__*/_jsx(PPUIGridItem, Object.assign({
          gridArea: name
        }, layoutProps, styleProps, overrideStyles, {
          children: Footer
        }), name);
      default:
        {
          name;
          console.warn(`Unknown grid area: "${name}"`);
          return null;
        }
    }
  };
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(PPUIGridItem, Object.assign({
      gridRow: `${gridAreaNames[0]}-start / ${gridAreaNames[gridAreaNames.length - 1]}-end`,
      cssBoxShadow: cssBoxShadow,
      cssScrollSnapAlign: scrollSnapAlign,
      zIndex: -1
    }, layoutProps, cardStylesTokens)), gridAreaNames.map((name, index) => {
      const overrideStyles = index === 0 || index === gridAreaNames.length - 1 ? {
        paddingBlockEnd: paddingBlock !== null && paddingBlock !== void 0 ? paddingBlock : padding
      } : {};
      return getGridItem(name, overrideStyles);
    }), !!Overlay && /*#__PURE__*/_jsx(PPUIGridItem, Object.assign({
      gridRow: `${gridAreaNames[0]}-start / ${gridAreaNames[gridAreaNames.length - 1]}-end`,
      zIndex: 1,
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      padding: padding,
      paddingBlock: paddingBlock,
      paddingInline: paddingInline,
      borderStartStartRadius: (_cardStylesTokens$bor = cardStylesTokens.borderStartStartRadius) !== null && _cardStylesTokens$bor !== void 0 ? _cardStylesTokens$bor : cardStylesTokens.borderRadius,
      borderStartEndRadius: (_cardStylesTokens$bor2 = cardStylesTokens.borderStartEndRadius) !== null && _cardStylesTokens$bor2 !== void 0 ? _cardStylesTokens$bor2 : cardStylesTokens.borderRadius,
      borderEndStartRadius: (_cardStylesTokens$bor3 = cardStylesTokens.borderEndStartRadius) !== null && _cardStylesTokens$bor3 !== void 0 ? _cardStylesTokens$bor3 : cardStylesTokens.borderRadius,
      borderEndEndRadius: (_cardStylesTokens$bor4 = cardStylesTokens.borderEndEndRadius) !== null && _cardStylesTokens$bor4 !== void 0 ? _cardStylesTokens$bor4 : cardStylesTokens.borderRadius,
      backgroundColor: "specialty-scrim-alt",
      "data-selenium-id": `${dataSeleniumId}-ineligible-overlay`
    }, layoutProps, styleProps, {
      children: Overlay
    }))]
  });
};
export default MerchCardLayout;